import { useQuery } from '@tanstack/react-query'
import { CalendarClock, CheckCircle2, Info, Moon } from 'lucide-react'
import { useState } from 'react'

import { businessDayQueryOptions } from '@/api/reference-api'
import { savingsProductListQueryOptions } from '@/api/savings-api'
import type { DormancyRun, InterestRun } from '@/api/savings-types'
import { MoneyDisplay } from '@/components/money-display'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { formatBusinessDate, formatShortDate } from '@/lib/datetime'

import { interestFrequencyLabel } from './constants'
import { usePostSavingsInterest, useRunDormancySweep } from './mutations'
import { SavingsProblemAlert } from './savings-errors'

/**
 * The two period-end runs.
 *
 * Both are branch-scoped batch operations that reach every account they match, which is why they
 * live on their own screen rather than as a button on an account: nothing on this page acts on one
 * customer, and it should not look as though it might.
 *
 * They are not the same kind of act. The interest run **posts a journal** — it capitalises accrued
 * interest into customer balances and into the interest-expense account, and it cannot be undone
 * from here. The dormancy sweep moves no money at all; it ages idle accounts into a status that
 * blocks debits until somebody reactivates them.
 */

const today = () => new Date().toISOString().slice(0, 10)

export function InterestRunPanel({ branchId }: { branchId: string | null }) {
  const [productId, setProductId] = useState('')
  const [asOf, setAsOf] = useState('')
  const [result, setResult] = useState<InterestRun | null>(null)

  const products = useQuery(savingsProductListQueryOptions())
  const businessDay = useQuery(businessDayQueryOptions(branchId))
  const run = usePostSavingsInterest()

  // A product that never posts interest has nothing for this run to do, and offering it would
  // produce a run of zero accounts that reads like a failure.
  const runnable = (products.data ?? []).filter(
    (product) => product.isActive && product.interestPostingFrequency !== 'None',
  )
  const product = runnable.find((candidate) => candidate.id === productId)

  return (
    <Card>
      <CardHeader>
        <div className="min-w-0">
          <CardTitle>Capitalise interest</CardTitle>
          <p className="mt-0.5 text-xs text-muted">
            Posts accrued interest for one product at this branch, as one journal.
          </p>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {result ? (
          <div className="space-y-3">
            <Alert tone={result.accountsPosted > 0 ? 'success' : 'info'}>
              {result.accountsPosted > 0 ? <CheckCircle2 aria-hidden /> : <Info aria-hidden />}
              <div className="space-y-1">
                <AlertTitle>
                  {result.accountsPosted > 0
                    ? `Interest posted to ${String(result.accountsPosted)} account(s)`
                    : 'Nothing to post'}
                </AlertTitle>
                <AlertDescription>
                  {result.accountsPosted > 0 ? (
                    <>
                      <MoneyDisplay
                        value={result.totalPostedMinor}
                        currency={result.currencyCode}
                        className="inline text-left"
                      />{' '}
                      capitalised as at {formatShortDate(result.asOf)}.{' '}
                      {result.journalEntryNumber === null ? (
                        <>No journal number came back with the run.</>
                      ) : (
                        <>
                          Journal{' '}
                          <span className="font-mono text-xs">{result.journalEntryNumber}</span>.
                        </>
                      )}
                    </>
                  ) : (
                    <>
                      No account on this product at this branch had interest to capitalise as at{' '}
                      {formatShortDate(result.asOf)}. That is the ordinary answer when a run has
                      already been made for the period.
                    </>
                  )}
                </AlertDescription>
              </div>
            </Alert>
            <Button
              variant="secondary"
              onClick={() => {
                setResult(null)
                run.reset()
              }}
            >
              Run another
            </Button>
          </div>
        ) : (
          <>
            <div className="space-y-1.5">
              <Label htmlFor="interest-product">Product</Label>
              <Select value={productId} onValueChange={setProductId}>
                <SelectTrigger id="interest-product">
                  <SelectValue placeholder={products.isPending ? 'Loading…' : 'Select a product'} />
                </SelectTrigger>
                <SelectContent>
                  {runnable.map((candidate) => (
                    <SelectItem key={candidate.id} value={candidate.id}>
                      {candidate.code} — {candidate.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {products.isError ? (
                <p className="text-xs text-danger-text">
                  The product list could not be read, so there is nothing to run against.
                </p>
              ) : runnable.length === 0 && !products.isPending ? (
                <p className="text-xs text-muted">
                  No active product posts interest. A product with a posting frequency of “never”
                  has nothing to capitalise.
                </p>
              ) : product ? (
                <p className="text-xs text-muted">
                  Posts {interestFrequencyLabel(product.interestPostingFrequency).toLowerCase()}.
                </p>
              ) : null}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="interest-as-of">As at (optional)</Label>
              <Input
                id="interest-as-of"
                type="date"
                value={asOf}
                max={today()}
                onChange={(event) => setAsOf(event.target.value)}
                aria-describedby="interest-as-of-help"
              />
              <p id="interest-as-of-help" className="text-xs text-muted">
                Left empty, the run takes the branch&apos;s current business date
                {businessDay.data?.businessDate
                  ? ` — ${formatBusinessDate(businessDay.data.businessDate)}`
                  : ''}
                . A future date is refused with savings.value_date_out_of_window.
              </p>
            </div>

            {run.isError ? <SavingsProblemAlert error={run.error} /> : null}

            <Button
              variant="primary"
              disabled={productId === '' || branchId === null || run.isPending}
              onClick={() => {
                if (branchId === null) {
                  return
                }
                run.mutate(
                  { productId, branchId, asOf: asOf === '' ? null : asOf },
                  { onSuccess: setResult },
                )
              }}
            >
              <CalendarClock />
              {run.isPending ? 'Running…' : 'Capitalise interest'}
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  )
}

export function DormancySweepPanel({ branchId }: { branchId: string | null }) {
  const [asOf, setAsOf] = useState('')
  const [result, setResult] = useState<DormancyRun | null>(null)

  const businessDay = useQuery(businessDayQueryOptions(branchId))
  const sweep = useRunDormancySweep()

  return (
    <Card>
      <CardHeader>
        <div className="min-w-0">
          <CardTitle>Age idle accounts into dormancy</CardTitle>
          <p className="mt-0.5 text-xs text-muted">
            Moves no money. A dormant account refuses debits until it is reactivated.
          </p>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {result ? (
          <div className="space-y-3">
            <Alert tone="success">
              <Moon aria-hidden />
              <div className="space-y-1">
                <AlertTitle>
                  {result.accountsAged === 0
                    ? 'No account was idle enough to age'
                    : `${String(result.accountsAged)} account(s) aged into dormancy`}
                </AlertTitle>
                <AlertDescription>
                  As at {formatShortDate(result.asOf)}. Each account&apos;s idle period comes from
                  its own product, and security deposit accounts are never swept — they are not idle,
                  they are doing their job. Reactivating one is a single stated reason on the
                  account.
                </AlertDescription>
              </div>
            </Alert>
            <Button
              variant="secondary"
              onClick={() => {
                setResult(null)
                sweep.reset()
              }}
            >
              Run another
            </Button>
          </div>
        ) : (
          <>
            <div className="space-y-1.5">
              <Label htmlFor="dormancy-as-of">As at (optional)</Label>
              <Input
                id="dormancy-as-of"
                type="date"
                value={asOf}
                max={today()}
                onChange={(event) => setAsOf(event.target.value)}
                aria-describedby="dormancy-as-of-help"
              />
              <p id="dormancy-as-of-help" className="text-xs text-muted">
                Left empty, the sweep takes the branch&apos;s current business date
                {businessDay.data?.businessDate
                  ? ` — ${formatBusinessDate(businessDay.data.businessDate)}`
                  : ''}
                .
              </p>
            </div>

            {sweep.isError ? <SavingsProblemAlert error={sweep.error} /> : null}

            <Button
              variant="primary"
              disabled={branchId === null || sweep.isPending}
              onClick={() => {
                if (branchId === null) {
                  return
                }
                sweep.mutate({ branchId, asOf: asOf === '' ? null : asOf }, { onSuccess: setResult })
              }}
            >
              <Moon />
              {sweep.isPending ? 'Sweeping…' : 'Run the sweep'}
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  )
}
